// @ts-nocheck
/*
 * Call-centre-admin-only Agent Performance Report.
 * Scoped to this call centre's child agents. Team-lead filter shows
 * agents assigned to that lead via agent_team_lead_config.
 *

 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, RotateCcw, Search, X } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { Button } from '@/components/ui/button';
import { MultiSelect } from '@/components/ui/multi-select';
import { getApiCall } from '@/service/httpCall';
import { API_AGENT_PERFORMANCE_REPORT } from '@/service/url';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import moment from 'moment';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { HBarChart, REPORT_COLORS, ReportChartCard } from '@/components/charts/ReportCharts';
import { downloadCsv, exportLines, stampedFileName, visibleExportFields } from '@/components/data-table/csv-export';

const pad = (n) => String(n).padStart(2, '0');

const formatDuration = (totalSeconds) => {
  const s = Number(totalSeconds) || 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${pad(m)}m ${pad(sec)}s`;
  if (m > 0) return `${m}m ${pad(sec)}s`;
  return `${sec}s`;
};

const pctLabel = (value) => `${Number(value || 0).toFixed(2)}%`;

// One labelled value inside a stacked table cell.
const StatLine = ({ label, value, className = '' }) => (
  <div className="flex items-baseline gap-1.5 whitespace-nowrap text-xs">
    <span className="text-[10px] uppercase text-muted-foreground">{label}</span>
    <span className={'font-semibold tabular-nums ' + (className || 'text-foreground')}>{value}</span>
  </div>
);

const Stack = ({ items }) => (
  <div className="flex flex-col gap-0.5 leading-tight">
    {items.map(([label, value, className]) => (
      <StatLine key={label} label={label} value={value ?? 0} className={className} />
    ))}
  </div>
);

const MAX_BACK_DAYS = 7;
const DATE_FMT = 'YYYY-MM-DD';

// Stable references: a new [] on every render would make DataTable's onFilteredDataChange loop forever.
const EMPTY_ROWS = [];
const EMPTY_HOURS = [];

const HOUR_VALUES = Array.from({ length: 24 }, (_, h) => h);
// "From" shows the start of the hour, "To" shows where the last selected hour ends (value = last hour, inclusive).
const fromHourLabel = (h) => `${pad(h)}:00`;
const toHourLabel = (h) => `${pad(h + 1)}:00`;

const to12 = (h) => h % 12 || 12;
const hourLabel = (h) => `${to12(h)}-${to12(h + 1)} ${h < 12 ? 'am' : 'pm'}`;

const getDefaultFilters = () => ({
  date: moment().format(DATE_FMT),
  hourFrom: 0,
  hourTo: 23,
});

// `color` = rgb triplet for chip + cell tint; `avg` = [numerator, denominator] keys,
// averaged over whatever hours are summed (one hour or the whole range).
const HOURLY_METRICS = [
  { key: 'total', label: 'Total calls', short: 'Total', color: '59,130,246' },
  { key: 'incoming', label: 'Incoming', short: 'In', color: '14,165,233' },
  { key: 'outgoing', label: 'Outgoing', short: 'Out', color: '99,102,241' },
  { key: 'answered', label: 'Answered', short: 'Ans', color: '16,185,129' },
  { key: 'failed', label: 'Failed', short: 'Fail', color: '244,63,94' },
  { key: 'abandoned', label: 'Abandoned', short: 'Aband', color: '245,158,11' },
  { key: 'talk_seconds', label: 'Talk time', short: 'Talk', color: '139,92,246', duration: true },
  { key: 'avg_talk', label: 'Avg talk', short: 'Avg talk', color: '168,85,247', duration: true, avg: ['talk_seconds', 'answered'] },
  { key: 'ring_seconds', label: 'Ring time', short: 'Ring', color: '20,184,166', duration: true },
  { key: 'avg_ring', label: 'Avg ring', short: 'Avg ring', color: '6,182,212', duration: true, avg: ['ring_seconds', 'ring_count'] },
];
const DEFAULT_HOURLY_METRICS = ['total', 'answered', 'talk_seconds'];

const sumKey = (buckets, key) => buckets.reduce((s, b) => s + (Number(b?.[key]) || 0), 0);
const metricValue = (m, buckets) => {
  if (!m.avg) return sumKey(buckets, m.key);
  const den = sumKey(buckets, m.avg[1]);
  return den > 0 ? Math.round(sumKey(buckets, m.avg[0]) / den) : 0;
};
const showMetric = (m, v) => (m.duration ? formatDuration(v) : v);

const emptyTotals = () => ({
  total: 0,
  answered: 0,
  failed: 0,
  abandoned: 0,
  incoming: 0,
  outgoing: 0,
  total_talk_seconds: 0,
  avg_talk_seconds: 0,
  avg_handle_seconds: 0,
  total_wrap_seconds: 0,
  avg_wrap_seconds: 0,
  wrap_count: 0,
  answered_pct: 0,
  failed_pct: 0,
  abandoned_pct: 0,
  transfers: 0,
  conferences: 0,
  total_hold_seconds: 0,
  hold_count: 0,
  avg_hold_seconds: 0,
  total_mute_seconds: 0,
  mute_count: 0,
  avg_mute_seconds: 0,
  total_transfer_seconds: 0,
  avg_transfer_seconds: 0,
  total_conference_seconds: 0,
  avg_conference_seconds: 0,
  breaks_taken: 0,
  total_break_seconds: 0,
  avg_break_seconds: 0,
  exceeded_break_seconds: 0,
  exceeded_break_count: 0,
  login_count: 0,
  logout_count: 0,
  total_login_seconds: 0,
  productivity_seconds: 0,
  switch_profile_count: 0,
  sip_unregister_count: 0,
});

const pct = (part, whole) =>
  whole > 0 ? Math.round((part / whole) * 10000) / 100 : 0;

const totalsFromRows = (list) => {
  const acc = emptyTotals();
  (list || []).forEach((r) => {
    acc.total += Number(r.total) || 0;
    acc.answered += Number(r.answered) || 0;
    acc.failed += Number(r.failed) || 0;
    acc.abandoned += Number(r.abandoned) || 0;
    acc.incoming += Number(r.incoming) || 0;
    acc.outgoing += Number(r.outgoing) || 0;
    acc.total_talk_seconds += Number(r.total_talk_seconds) || 0;
    acc.total_wrap_seconds += Number(r.total_wrap_seconds) || 0;
    acc.wrap_count += Number(r.wrap_count) || 0;
    acc.transfers += Number(r.transfers) || 0;
    acc.conferences += Number(r.conferences) || 0;
    acc.total_hold_seconds += Number(r.total_hold_seconds) || 0;
    acc.hold_count += Number(r.hold_count) || 0;
    acc.total_mute_seconds += Number(r.total_mute_seconds) || 0;
    acc.mute_count += Number(r.mute_count) || 0;
    acc.total_transfer_seconds += Number(r.total_transfer_seconds) || 0;
    acc.total_conference_seconds += Number(r.total_conference_seconds) || 0;
    acc.breaks_taken += Number(r.breaks_taken) || 0;
    acc.total_break_seconds += Number(r.total_break_seconds) || 0;
    acc.exceeded_break_seconds += Number(r.exceeded_break_seconds) || 0;
    acc.exceeded_break_count += Number(r.exceeded_break_count) || 0;
    acc.login_count += Number(r.login_count) || 0;
    acc.logout_count += Number(r.logout_count) || 0;
    acc.total_login_seconds += Number(r.total_login_seconds) || 0;
    acc.productivity_seconds += Number(r.productivity_seconds) || 0;
    acc.switch_profile_count += Number(r.switch_profile_count) || 0;
    acc.sip_unregister_count += Number(r.sip_unregister_count) || 0;
  });
  acc.answered_pct = pct(acc.answered, acc.total);
  acc.failed_pct = pct(acc.failed, acc.total);
  acc.abandoned_pct = pct(acc.abandoned, acc.total);
  acc.avg_talk_seconds =
    acc.answered > 0 ? Math.round(acc.total_talk_seconds / acc.answered) : 0;
  acc.avg_handle_seconds =
    acc.answered > 0
      ? Math.round((acc.total_talk_seconds + acc.total_wrap_seconds) / acc.answered)
      : 0;
  acc.avg_wrap_seconds =
    acc.wrap_count > 0 ? Math.round(acc.total_wrap_seconds / acc.wrap_count) : 0;
  acc.avg_hold_seconds =
    acc.hold_count > 0 ? Math.round(acc.total_hold_seconds / acc.hold_count) : 0;
  acc.avg_mute_seconds =
    acc.mute_count > 0 ? Math.round(acc.total_mute_seconds / acc.mute_count) : 0;
  acc.avg_transfer_seconds =
    acc.transfers > 0 ? Math.round(acc.total_transfer_seconds / acc.transfers) : 0;
  acc.avg_conference_seconds =
    acc.conferences > 0
      ? Math.round(acc.total_conference_seconds / acc.conferences)
      : 0;
  acc.avg_break_seconds =
    acc.breaks_taken > 0 ? Math.round(acc.total_break_seconds / acc.breaks_taken) : 0;
  return acc;
};

const KPI_GROUPS = [
  {
    title: 'Calls',
    wide: true,
    cards: [
      { key: 'total', label: 'Total Calls', className: 'text-blue-600 dark:text-blue-400' },
      { key: 'incoming', label: 'Incoming', className: 'text-sky-600 dark:text-sky-400' },
      { key: 'outgoing', label: 'Outgoing', className: 'text-orange-600 dark:text-orange-400' },
      { key: 'answered', label: 'Answered', className: 'text-emerald-600 dark:text-emerald-400', pctKey: 'answered_pct' },
      { key: 'failed', label: 'Failed', className: 'text-rose-600 dark:text-rose-400', pctKey: 'failed_pct' },
      { key: 'abandoned', label: 'Abandoned', className: 'text-amber-600 dark:text-amber-400', pctKey: 'abandoned_pct' },
    ],
  },
  {
    title: 'Talk & wrap',
    cards: [
      { key: 'total_talk_seconds', label: 'Talk Time', isDuration: true },
      { key: 'avg_handle_seconds', label: 'Avg Handle', isDuration: true },
      { key: 'total_wrap_seconds', label: 'Total Wrap', isDuration: true },
      { key: 'avg_wrap_seconds', label: 'Avg Wrap', isDuration: true },
    ],
  },
  {
    title: 'Login',
    sessionNote: true,
    cards: [
      { key: 'total_login_seconds', label: 'Login Time', isDuration: true },
      { key: 'productivity_seconds', label: 'Productivity', isDuration: true },
      { key: 'switch_profile_count', label: 'Profile Switches' },
      { key: 'sip_unregister_count', label: 'SIP Unregister' },
    ],
  },
  {
    title: 'Breaks',
    cards: [
      { key: 'breaks_taken', label: 'Breaks Taken' },
      { key: 'total_break_seconds', label: 'Total Break', isDuration: true },
      { key: 'exceeded_break_seconds', label: 'Exceeded Break', className: 'text-rose-600 dark:text-rose-400', isDuration: true },
    ],
  },
  {
    title: 'In-call',
    cards: [
      { key: 'total_hold_seconds', label: 'Hold Time', isDuration: true },
      { key: 'total_mute_seconds', label: 'Mute Time', isDuration: true },
      { key: 'total_transfer_seconds', label: 'Transfer Time', isDuration: true },
      { key: 'total_conference_seconds', label: 'Conference Time', isDuration: true },
    ],
  },
];

const AgentPerformanceReportPage = () => {
  const defaults = useMemo(() => getDefaultFilters(), []);


  const [view, setView] = useState('summary');
  const [date, setDate] = useState(defaults.date);
  const [hourFrom, setHourFrom] = useState(defaults.hourFrom);
  const [hourTo, setHourTo] = useState(defaults.hourTo);
  const [teamLeadId, setTeamLeadId] = usePersistedState('ivr-reports/AgentPerformance:teamLeadId', '');
  const [selectedAgents, setSelectedAgents] = usePersistedState('ivr-reports/AgentPerformance:selectedAgents', []);
  const [metricKeys, setMetricKeys] = usePersistedState('ivr-reports/AgentPerformance:hourlyMetrics', DEFAULT_HOURLY_METRICS);

  
  const [applied, setApplied] = useState(() => ({
    date: defaults.date,
    hourFrom: defaults.hourFrom,
    hourTo: defaults.hourTo,
    teamLeadId,
    selectedAgents,
  }));
  const appliedKey = JSON.stringify(applied);

  const [summary, setSummary] = useState(null);
  const [hourly, setHourly] = useState(null);
  const [filteredRows, setFilteredRows] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);
  const [teamLeadOptions, setTeamLeadOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const summaryRows = summary?.agents ?? EMPTY_ROWS;
  const hours = hourly?.hours ?? EMPTY_HOURS;
  const rows = useMemo(() => {
    if (view !== 'hourly') return summaryRows;
    const byId = new Map((hourly?.agents || []).map((a) => [String(a.agent_id), a]));
    return summaryRows.map((r) => ({ ...r, hours: byId.get(String(r.agent_id))?.hours || {} }));
  }, [view, summaryRows, hourly]);
  const totals = useMemo(() => summary?.totals || emptyTotals(), [summary]);

  const minDate = useMemo(() => moment().subtract(MAX_BACK_DAYS, 'days').format(DATE_FMT), []);
  const maxDate = useMemo(() => moment().format(DATE_FMT), []);

  const loadedRef = useRef({ summary: '', hourly: '' }); 
  const reqRef = useRef(0); 

  const selectedLead = useMemo(
    () => teamLeadOptions.find((t) => String(t.id) === String(teamLeadId)) || null,
    [teamLeadOptions, teamLeadId],
  );

  const filteredAgentOptions = useMemo(() => {
    const all = agentOptions.map((a) => ({
      value: String(a.id),
      label: a.agent_name || `Agent #${a.id}`,
    }));
    if (!selectedLead) return all;
    const allowed = new Set((selectedLead.agent_ids || []).map(String));
    return all.filter((o) => allowed.has(o.value));
  }, [agentOptions, selectedLead]);

  const handleTeamLeadChange = (value) => {
    setTeamLeadId(value);
    if (!value) return;
    const lead = teamLeadOptions.find((t) => String(t.id) === String(value));
    const allowed = new Set((lead?.agent_ids || []).map(String));
    setSelectedAgents((prev) => prev.filter((id) => allowed.has(id)));
  };

  const handleHourFromChange = (value) => {
    const next = Number(value);
    setHourFrom(next);
    if (next > hourTo) setHourTo(next);
  };

  const handleHourToChange = (value) => {
    const next = Number(value);
    setHourTo(next);
    if (next < hourFrom) setHourFrom(next);
  };

  // force = Filter clicked with unchanged filters: refetch everything.
  // Otherwise only fetch what this view is missing (tab switch reuses the loaded summary).
  const load = useCallback(async (targetView, filters, force = false) => {
    const reqId = ++reqRef.current;
    const key = JSON.stringify(filters);
    if (force) loadedRef.current = { summary: '', hourly: '' };
    const needSummary = loadedRef.current.summary !== key;
    const needHourly = targetView === 'hourly';
    setLoading(true);
    setError('');

    const params = new URLSearchParams();
    params.append('date', filters.date);
    params.append('hour_from', String(filters.hourFrom));
    params.append('hour_to', String(filters.hourTo));
    if (filters.teamLeadId) params.append('team_lead_id', filters.teamLeadId);
    if (filters.selectedAgents.length) params.append('agent_ids', filters.selectedAgents.join(','));

    const buildUrl = (base) => `${base}?${params.toString()}`;

    try {
      const [sumResp, hourResp] = await Promise.all([
        needSummary ? getApiCall(buildUrl(API_AGENT_PERFORMANCE_REPORT)) : Promise.resolve(null),
        needHourly
          ? getApiCall(buildUrl(`${API_AGENT_PERFORMANCE_REPORT}/hourly`))
          : Promise.resolve(null),
      ]);
      if (reqId !== reqRef.current) return;
      if (needSummary) {
        setSummary(sumResp || null);
        loadedRef.current.summary = key;
      }
      if (needHourly) {
        setHourly(hourResp || null);
        loadedRef.current.hourly = key;
      }
      const meta = sumResp || hourResp;
      if (Array.isArray(meta?.agent_options)) setAgentOptions(meta.agent_options);
      if (Array.isArray(meta?.team_lead_options)) setTeamLeadOptions(meta.team_lead_options);
    } catch (err) {
      if (reqId !== reqRef.current) return;
      if (needSummary) setSummary(null);
      if (needHourly) setHourly(null);
      // getApiCall already toasts server errors; only say the reason here when it didn't.
      const toasted = typeof err === 'string' && !/^(A network error|Session Expired)/.test(err);
      setError(
        toasted
          ? 'Click Filter to try again.'
          : typeof err === 'string'
            ? friendlyApiError(err)
            : 'Failed to load agent performance report',
      );
    } finally {
      if (reqId === reqRef.current) setLoading(false);
    }
  }, []);

  
  useEffect(() => {
    if (loadedRef.current[view] === appliedKey) return;
    load(view, applied);
  }, [view, appliedKey]);

  const applyFilters = (next) => {
    if (JSON.stringify(next) === appliedKey) load(view, next, true);
    else setApplied(next);
  };

  const handleApply = () => applyFilters({ date, hourFrom, hourTo, teamLeadId, selectedAgents });

  const handleReset = () => {
    const d = getDefaultFilters();
    setDate(d.date);
    setHourFrom(d.hourFrom);
    setHourTo(d.hourTo);
    setTeamLeadId('');
    setSelectedAgents([]);
    applyFilters({ ...d, teamLeadId: '', selectedAgents: [] });
  };

  const displayTotals = useMemo(
    () => (loading ? totals : totalsFromRows(filteredRows)),
    [loading, totals, filteredRows],
  );

  /* ── Charts: from the rows the table currently shows (filters + search applied). ── */
  // How many agents the charts plot; the chart height grows with it. 0 = all.
  const TOP_AGENT_OPTIONS = [8, 15, 25, 0];
  const [topAgents, setTopAgents] = usePersistedState('ivr-reports/AgentPerformance:topAgents', 8);
  const TOP_AGENTS = topAgents || Infinity;
  const [showCharts, setShowCharts] = useState(false);
  const agentLabel = (r) => r.agent_name || r.agent_number || `#${r.agent_id ?? r.id ?? ''}`;
  const outcomeByAgent = useMemo(
    () =>
      [...filteredRows]
        .sort((a, b) => Number(b.total || 0) - Number(a.total || 0))
        .slice(0, TOP_AGENTS)
        .filter((r) => Number(r.total || 0) > 0)
        .map((r) => ({
          agent: agentLabel(r),
          answered: Number(r.answered || 0),
          failed: Number(r.failed || 0),
          abandoned: Number(r.abandoned || 0),
        })),
    [filteredRows, TOP_AGENTS],
  );
  const talkByAgent = useMemo(
    () =>
      [...filteredRows]
        .sort((a, b) => Number(b.total_talk_seconds || 0) - Number(a.total_talk_seconds || 0))
        .slice(0, TOP_AGENTS)
        .filter((r) => Number(r.total_talk_seconds || 0) > 0)
        .map((r) => ({ agent: agentLabel(r), talk: Number(r.total_talk_seconds || 0) })),
    [filteredRows, TOP_AGENTS],
  );
  const agentCountNote =
    filteredRows.length > TOP_AGENTS ? `Top ${TOP_AGENTS} of ${filteredRows.length} agents` : 'All agents in view';

  // Stacked cells: one column per topic, every value labelled. The accessor is the
  // topic's main number (sort / filter); CSV export splits back into flat columns.
  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'agent_name',
        header: 'Agent',
        cell: ({ row }) => {
          const r = row.original;
          return (
            <div className="flex min-w-[150px] flex-col gap-0.5 leading-tight">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-foreground">{r.agent_name}</span>
                {r.access === 'team_lead' ? (
                  <span className="rounded-full bg-violet-100 px-1.5 py-px text-[10px] font-medium text-violet-700 dark:bg-violet-500/20 dark:text-violet-300">
                    Team Lead
                  </span>
                ) : null}
              </div>
              {r.agent_number ? <StatLine label="No." value={r.agent_number} /> : null}
              <StatLine label="TL" value={r.team_leads || '—'} />
            </div>
          );
        },
      },
      {
        accessorKey: 'total',
        header: 'Calls',
        meta: { filter: 'number' },
        cell: ({ row }) => (
          <Stack
            items={[
              ['Total', row.original.total, 'text-blue-600 dark:text-blue-400'],
              ['In', row.original.incoming],
              ['Out', row.original.outgoing],
            ]}
          />
        ),
      },
      {
        accessorKey: 'answered',
        header: 'Outcome',
        meta: { filter: 'number' },
        cell: ({ row }) => {
          const r = row.original;
          return (
            <Stack
              items={[
                ['Ans', `${r.answered} · ${pctLabel(r.answered_pct)}`, 'text-emerald-600 dark:text-emerald-400'],
                ['Fail', `${r.failed} · ${pctLabel(r.failed_pct)}`, 'text-rose-600 dark:text-rose-400'],
                ['Aband', `${r.abandoned} · ${pctLabel(r.abandoned_pct)}`, 'text-amber-600 dark:text-amber-400'],
              ]}
            />
          );
        },
      },
      {
        accessorKey: 'total_talk_seconds',
        header: 'Talk',
        meta: { filter: 'duration' },
        cell: ({ row }) => {
          const r = row.original;
          return (
            <Stack
              items={[
                ['Total', formatDuration(r.total_talk_seconds)],
                ['Avg talk', formatDuration(r.avg_talk_seconds)],
                ['Avg handle', formatDuration(r.avg_handle_seconds)],
                ['Avg ring', formatDuration(r.avg_ring_seconds)],
              ]}
            />
          );
        },
      },
      {
        accessorKey: 'total_wrap_seconds',
        header: 'Wrap',
        meta: { filter: 'duration' },
        cell: ({ row }) => {
          const r = row.original;
          return (
            <Stack
              items={[
                ['Total', formatDuration(r.total_wrap_seconds)],
                ['Avg', formatDuration(r.avg_wrap_seconds)],
                ['Count', r.wrap_count],
              ]}
            />
          );
        },
      },
      {
        accessorKey: 'total_hold_seconds',
        header: 'Hold / Mute',
        meta: { filter: 'duration' },
        cell: ({ row }) => {
          const r = row.original;
          return (
            <Stack
              items={[
                ['Hold', formatDuration(r.total_hold_seconds)],
                ['Avg hold', formatDuration(r.avg_hold_seconds)],
                ['Mute', formatDuration(r.total_mute_seconds)],
                ['Avg mute', formatDuration(r.avg_mute_seconds)],
              ]}
            />
          );
        },
      },
      {
        accessorKey: 'transfers',
        header: 'Transfer / Conf',
        meta: { filter: 'number' },
        cell: ({ row }) => {
          const r = row.original;
          return (
            <Stack
              items={[
                ['Transfers', `${r.transfers} · ${formatDuration(r.total_transfer_seconds)}`],
                ['Avg transfer', formatDuration(r.avg_transfer_seconds)],
                ['Conferences', `${r.conferences} · ${formatDuration(r.total_conference_seconds)}`],
                ['Avg conf', formatDuration(r.avg_conference_seconds)],
              ]}
            />
          );
        },
      },
      {
        accessorKey: 'breaks_taken',
        header: 'Breaks',
        meta: { filter: 'number' },
        cell: ({ row }) => {
          const r = row.original;
          const exceeded = Number(r.exceeded_break_seconds) > 0;
          return (
            <Stack
              items={[
                ['Taken', r.breaks_taken],
                ['Total', formatDuration(r.total_break_seconds)],
                ['Avg', formatDuration(r.avg_break_seconds)],
                [
                  'Exceeded',
                  `${formatDuration(r.exceeded_break_seconds)} · ${r.exceeded_break_count}x`,
                  exceeded ? 'text-rose-600 dark:text-rose-400' : undefined,
                ],
              ]}
            />
          );
        },
      },
      {
        accessorKey: 'total_login_seconds',
        header: 'Login',
        meta: { filter: 'duration' },
        cell: ({ row }) => {
          const r = row.original;
          return (
            <Stack
              items={[
                ['Login', formatDuration(r.total_login_seconds)],
                ['Productive', formatDuration(r.productivity_seconds)],
                ['In / Out', `${r.login_count} / ${r.logout_count}`],
                ['Switches', r.switch_profile_count],
                ['SIP unreg', r.sip_unregister_count],
              ]}
            />
          );
        },
      },
    ],
    [],
  );

  
  // Picked metrics in fixed list order; a stale/empty saved pick falls back to the default.
  const selectedMetrics = useMemo(() => {
    const picked = HOURLY_METRICS.filter((m) => metricKeys?.includes(m.key));
    return picked.length ? picked : HOURLY_METRICS.filter((m) => DEFAULT_HOURLY_METRICS.includes(m.key));
  }, [metricKeys]);
  const sortMetric = selectedMetrics[0];
  const toggleMetric = (key) =>
    setMetricKeys(() => {
      const cur = selectedMetrics.map((m) => m.key);
      if (!cur.includes(key)) return [...cur, key];
      return cur.length > 1 ? cur.filter((k) => k !== key) : cur; // keep at least one
    });

  // Per-metric max across all agents/hours -> cell tint strength.
  const hourMax = useMemo(() => {
    const max = {};
    selectedMetrics.forEach((m) => {
      max[m.key] = 0;
      rows.forEach((r) => hours.forEach((h) => {
        max[m.key] = Math.max(max[m.key], metricValue(m, [r.hours?.[h]]));
      }));
    });
    return max;
  }, [rows, hours, selectedMetrics]);

  // One line per picked metric, zeros included (0 / 0s, muted); a single pick drops the label.
  const renderMetricLines = (buckets, bold = false) => {
    const values = selectedMetrics.map((m) => [m, metricValue(m, buckets)]);
    const single = values.length === 1;
    return (
      <div className="flex min-w-[96px] flex-col gap-0.5 leading-tight">
        {values.map(([m, v]) => {
          const alpha = !bold && v && hourMax[m.key] > 0 ? 0.1 + 0.45 * (v / hourMax[m.key]) : 0;
          return (
            <div key={m.key} className="flex items-center justify-between gap-2 whitespace-nowrap text-xs">
              {single ? null : <span className="text-[10px] uppercase text-muted-foreground">{m.short}</span>}
              <span
                className={'rounded px-1.5 tabular-nums ' + (bold ? 'font-bold' : 'font-semibold') + (v ? ' text-foreground' : ' text-muted-foreground/50')}
                style={alpha ? { backgroundColor: `rgba(${m.color},${alpha.toFixed(2)})` } : undefined}
              >
                {showMetric(m, v)}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const hourColumns = useMemo<ColumnDef<any>[]>(
    () =>
      hours.map((h) => ({
        id: `hour_${h}`,
        header: hourLabel(h),
        // Sort/filter by the first picked metric.
        accessorFn: (r) => metricValue(sortMetric, [r.hours?.[h]]),
        meta: { filter: sortMetric.duration ? 'duration' : 'number' },
        cell: ({ row }) => renderMetricLines([row.original.hours?.[h]]),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hours, selectedMetrics, hourMax],
  );

  // Hourly view: agent + hour columns + range total of the picked metrics; the summary
  // columns stay on the Summary tab.
  const tableColumns = useMemo(() => {
    if (view !== 'hourly') return columns;
    const agentCol = columns.find((c) => c.accessorKey === 'agent_name');
    const rangeBuckets = (r) => hours.map((h) => r.hours?.[h]);
    const totalCol: ColumnDef<any> = {
      id: 'hour_total',
      header: 'Range total',
      accessorFn: (r) => metricValue(sortMetric, rangeBuckets(r)),
      meta: { filter: sortMetric.duration ? 'duration' : 'number' },
      cell: ({ row }) => renderMetricLines(rangeBuckets(row.original), true),
    };
    return [agentCol, ...hourColumns, totalCol];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, columns, hourColumns, hours, selectedMetrics]);

  // CSV: one flat column per value. Duration fields carry a 3rd `true` flag; handleExport
  // turns them into plain numbers with the unit in the header so Excel filters work.
  const exportSplit = useMemo(() => {
    const f = (header, key) => [header, (r) => r[key]];
    const d = (header, key) => [header, (r) => Number(r[key]) || 0, true];
    return {
      agent_name: [f('Agent', 'agent_name'), [ 'Role', (r) => (r.access === 'team_lead' ? 'Team Lead' : 'Agent') ], f('Agent Number', 'agent_number'), f('Team Lead', 'team_leads')],
      total: [f('Total', 'total'), f('Incoming', 'incoming'), f('Outgoing', 'outgoing')],
      answered: [f('Answered', 'answered'), f('Failed', 'failed'), f('Abandoned', 'abandoned'), f('Answered %', 'answered_pct'), f('Failed %', 'failed_pct'), f('Abandoned %', 'abandoned_pct')],
      total_talk_seconds: [d('Talk Time', 'total_talk_seconds'), d('Avg Talk', 'avg_talk_seconds'), d('Avg Handle', 'avg_handle_seconds'), d('Avg Ring', 'avg_ring_seconds')],
      total_wrap_seconds: [d('Total Wrap', 'total_wrap_seconds'), d('Avg Wrap', 'avg_wrap_seconds'), f('Wrap Count', 'wrap_count')],
      total_hold_seconds: [d('Hold Time', 'total_hold_seconds'), d('Avg Hold', 'avg_hold_seconds'), d('Mute Time', 'total_mute_seconds'), d('Avg Mute', 'avg_mute_seconds')],
      transfers: [f('Transfers', 'transfers'), d('Transfer Time', 'total_transfer_seconds'), d('Avg Transfer', 'avg_transfer_seconds'), f('Conferences', 'conferences'), d('Conference Time', 'total_conference_seconds'), d('Avg Conference', 'avg_conference_seconds')],
      breaks_taken: [f('Breaks Taken', 'breaks_taken'), d('Total Break', 'total_break_seconds'), d('Avg Break', 'avg_break_seconds'), d('Exceeded Break', 'exceeded_break_seconds'), f('Exceeded Breaks', 'exceeded_break_count')],
      total_login_seconds: [d('Login Time', 'total_login_seconds'), d('Productivity', 'productivity_seconds'), f('Logins', 'login_count'), f('Logouts', 'logout_count'), f('Profile Switches', 'switch_profile_count'), f('SIP Unregister', 'sip_unregister_count')],
    };
  }, []);

  // Hourly CSV: each picked metric for every hour as its own column (matches the screen).
  const hourlyExportSplit = useMemo(() => {
    const split = { agent_name: exportSplit.agent_name };
    hours.forEach((h) => {
      split[`hour_${h}`] = selectedMetrics.map((m) => [
        `${hourLabel(h)} ${m.label}`,
        (r) => metricValue(m, [r.hours?.[h]]),
        m.duration,
      ]);
    });
    split.hour_total = selectedMetrics.map((m) => [
      `Range ${m.label}`,
      (r) => metricValue(m, hours.map((h) => r.hours?.[h])),
      m.duration,
    ]);
    return split;
  }, [exportSplit, hours, selectedMetrics]);

  const handleExport = (table) => {
    const fields = visibleExportFields(table, view === 'hourly' ? hourlyExportSplit : exportSplit);
    const list = table.getFilteredRowModel().rows.map((row) => row.original);
    // Durations always in seconds (one unit across the whole file), unit in the header.
    const withUnits = fields.map(([header, get, isDuration]) => [isDuration ? `${header} (s)` : header, get]);
    downloadCsv(
      exportLines(withUnits, list),
      stampedFileName(view === 'hourly' ? 'agent-performance-hourly' : 'agent-performance'),
    );
  };

  // Toggle chips: picked = tinted with X, others = outline with +. Last one can't be removed.
  const metricPicker =
    view === 'hourly' ? (
      <div className="mb-2 flex shrink-0 flex-wrap items-center gap-1.5 rounded-lg border border-border/60 bg-card px-3 py-2">
        <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Metrics
        </span>
        {HOURLY_METRICS.map((m) => {
          const on = selectedMetrics.some((s) => s.key === m.key);
          const locked = on && selectedMetrics.length === 1;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => toggleMetric(m.key)}
              disabled={locked}
              aria-pressed={on}
              title={locked ? 'At least one metric is needed' : on ? `Remove ${m.label}` : `Add ${m.label}`}
              className={
                'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-colors disabled:cursor-not-allowed ' +
                (on ? 'text-foreground' : 'border-dashed border-border text-muted-foreground hover:bg-muted/60 hover:text-foreground')
              }
              style={on ? { backgroundColor: `rgba(${m.color},0.15)`, borderColor: `rgba(${m.color},0.55)` } : undefined}
            >
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: `rgb(${m.color})` }} />
              {m.label}
              {on ? (locked ? null : <X className="h-3 w-3 opacity-70" />) : <Plus className="h-3 w-3 opacity-70" />}
            </button>
          );
        })}
        <span className="ml-auto flex gap-1">
          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setMetricKeys(HOURLY_METRICS.map((m) => m.key))}>
            All
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setMetricKeys(DEFAULT_HOURLY_METRICS)}>
            Reset
          </Button>
        </span>
      </div>
    ) : null;

  return (
    <FillPage>
      <PageHeader
        title="Agent Performance Report"
        description="Per-agent calls, talk, wrap, breaks and login for one day (last 7 days)."
      />

      {/* Scrolls as a whole so the charts never squeeze the table to zero height. */}
      <div className="mt-4 flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="mb-3 flex shrink-0 flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Date
            </label>
            <CalendarDateInput
              value={date}
              min={minDate}
              max={maxDate}
              disabled={loading}
              aria-label="Report date"
              className="form-control"
              style={{ height: '40px', width: '170px' }}
              onChange={(v) => v && setDate(v)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              From hour
            </label>
            <select
              value={hourFrom}
              onChange={(e) => handleHourFromChange(e.target.value)}
              disabled={loading}
              className="input-embossed h-10 w-[110px] rounded-md px-3 text-sm"
            >
              {HOUR_VALUES.map((h) => (
                <option key={h} value={h}>
                  {fromHourLabel(h)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              To hour
            </label>
            <select
              value={hourTo}
              onChange={(e) => handleHourToChange(e.target.value)}
              disabled={loading}
              className="input-embossed h-10 w-[110px] rounded-md px-3 text-sm"
            >
              {HOUR_VALUES.map((h) => (
                <option key={h} value={h}>
                  {toHourLabel(h)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Team Lead
            </label>
            <select
              value={teamLeadId}
              onChange={(e) => handleTeamLeadChange(e.target.value)}
              className="input-embossed h-10 w-[200px] rounded-md px-3 text-sm"
            >
              <option value="">All team leads</option>
              {teamLeadOptions.map((lead) => (
                <option key={lead.id} value={lead.id}>
                  {lead.agent_name || `Lead #${lead.id}`}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Agent
            </label>
            <MultiSelect
              options={filteredAgentOptions}
              value={selectedAgents}
              onChange={setSelectedAgents}
              placeholder={teamLeadId ? 'Agents under lead' : 'All agents'}
              searchPlaceholder="Search agent…"
              emptyMessage={
                teamLeadId
                  ? 'No agents assigned to this team lead'
                  : 'No agents available'
              }
              maxChips={2}
              className="w-[260px]"
            />
          </div>
          <Button type="button" size="sm" onClick={handleApply} disabled={loading}>
            <Search className="h-3.5 w-3.5" />
            Filter
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={handleReset} disabled={loading}>
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </Button>
        </div>

        <div className="mb-3 flex shrink-0 flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-border/60 bg-muted/40 p-0.5">
            <Button
              type="button"
              size="sm"
              variant={view === 'summary' ? 'default' : 'ghost'}
              onClick={() => setView('summary')}
            >
              Summary
            </Button>
            <Button
              type="button"
              size="sm"
              variant={view === 'hourly' ? 'default' : 'ghost'}
              onClick={() => setView('hourly')}
            >
              Hourly
            </Button>
          </div>
          <span className="text-xs text-muted-foreground">
            {moment(applied.date, DATE_FMT).format('DD MMM YYYY')} · {fromHourLabel(applied.hourFrom)}–
            {toHourLabel(applied.hourTo)}
            {view === 'hourly' ? ` · ${selectedMetrics.map((m) => m.label).join(', ')} per hour` : ''}
          </span>
        </div>

        <div className="mb-4 grid shrink-0 gap-3 md:grid-cols-2 xl:grid-cols-4">
          {KPI_GROUPS.map((group) => (
            <section
              key={group.title}
              className={
                'rounded-lg border border-border/60 bg-card px-3 py-2.5 ' +
                (group.wide ? 'md:col-span-2 xl:col-span-4' : '')
              }
            >
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {group.title}
                {group.sessionNote && summary?.session_stats_scope === 'day' ? (
                  <span
                    className="ml-1 font-normal normal-case tracking-normal"
                    title="Login time, productivity, profile switches and SIP unregister are daily figures and are not limited by the hour range."
                  >
                    · whole day
                  </span>
                ) : null}
              </h3>
              <div
                className={
                  'grid gap-x-4 gap-y-2 ' +
                  (group.wide ? 'grid-cols-3 sm:grid-cols-6' : 'grid-cols-2')
                }
              >
                {group.cards.map((card) => (
                  <div key={card.key} className="min-w-0">
                    <div className="truncate text-[10px] font-medium uppercase text-muted-foreground">
                      {card.label}
                    </div>
                    <div className={'text-lg font-bold tabular-nums ' + (card.className || 'text-foreground')}>
                      {card.isDuration
                        ? formatDuration(displayTotals[card.key])
                        : displayTotals[card.key] ?? 0}
                      {card.pctKey ? (
                        <span className="ml-1.5 text-[11px] font-medium text-muted-foreground">
                          {pctLabel(displayTotals[card.pctKey])}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mb-2 flex shrink-0 items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Agent charts
          </span>
          <div className="flex items-center gap-2">
            {showCharts ? (
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                Agents
                <select
                  value={topAgents}
                  onChange={(e) => setTopAgents(Number(e.target.value))}
                  className="input-embossed h-8 rounded-md px-2 text-xs"
                >
                  {TOP_AGENT_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n ? `Top ${n}` : 'All'}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <Button variant="ghost" size="sm" onClick={() => setShowCharts((v) => !v)}>
              {showCharts ? 'Hide charts' : 'Show charts'}
            </Button>
          </div>
        </div>
        {showCharts ? (
          <div className="mb-4 grid shrink-0 gap-3 lg:grid-cols-2">
            <ReportChartCard title="Call outcomes by agent" subtitle={`${agentCountNote} · by total calls`}>
              <HBarChart
                data={outcomeByAgent}
                categoryKey="agent"
                rowHeight={18}
                series={[
                  { key: 'answered', label: 'Answered', color: REPORT_COLORS.good },
                  { key: 'failed', label: 'Failed', color: REPORT_COLORS.crit },
                  { key: 'abandoned', label: 'Abandoned', color: REPORT_COLORS.warn },
                ]}
                emptyLabel={loading ? 'Loading…' : 'No agent calls for these filters'}
              />
            </ReportChartCard>
            <ReportChartCard title="Talk time by agent" subtitle={`${agentCountNote} · by talk time`}>
              <HBarChart
                data={talkByAgent}
                categoryKey="agent"
                rowHeight={22}
                series={[{ key: 'talk', label: 'Talk time', color: REPORT_COLORS.call }]}
                valueFormatter={formatDuration}
                emptyLabel={loading ? 'Loading…' : 'No talk time for these filters'}
              />
            </ReportChartCard>
          </div>
        ) : null}

        {metricPicker}
        <div className="mb-4 min-h-[560px] flex-1 shrink-0">
          {/* v2 ids: old saved prefs referenced the 40 flat columns. */}
          <DataTable
            key={view}
            tableId={view === 'hourly' ? 'reports.agent-performance.hourly.v2' : 'reports.agent-performance.per-agent.v2'}
            columns={tableColumns}
            data={rows}
            isLoading={loading}
            searchPlaceholder="Search agents…"
            searchColumnId="agent_name"
            emptyTitle={error ? 'Could not load the report' : 'No agent performance data'}
            emptyDescription={error || 'No agent call data for the selected filters.'}
            pageSize={10}
            onExport={handleExport}
            initialSorting={[{ id: view === 'hourly' ? 'hour_total' : 'total', desc: true }]}
            initialColumnPinning={{ left: ['agent_name'] }}
            onFilteredDataChange={setFilteredRows}
          />
        </div>
      </div>
    </FillPage>
  );
};

export default AgentPerformanceReportPage;
